import { SplitFactory } from '@splitsoftware/splitio'
import { FEATURE_FLAG_CONFIG, FeatureFlag, TrafficType } from '../feature-flags'

export class FeatureFlagManager {
  private factory: SplitIO.ISDK
  private userClient: SplitIO.IClient | undefined
  private orgClient: SplitIO.IClient | undefined
  private isInitialized: boolean = false
  private initializationPromise: Promise<void> | undefined

  constructor(userId?: string, tenantId?: string) {
    if (!process.env.REACT_APP_SPLIT_IO_CLIENT_KEY)
      throw new Error('Feature flag configuration not set.')

    this.factory = SplitFactory({
      core: {
        authorizationKey: process.env.REACT_APP_SPLIT_IO_CLIENT_KEY,
        key: userId || 'key',
        trafficType: TrafficType.USER,
      },
      startup: {
        readyTimeout: 5, // seconds
      },
      streamingEnabled: true,
    })

    try {
      this.userClient = this.factory.client()
      this.orgClient = this.factory.client(tenantId || 'key', TrafficType.ORG)
    } catch (e) {
      console.error(e)
    }
  }

  private async initialize(): Promise<void> {
    if (this.isInitialized) return

    if (this.initializationPromise) {
      return this.initializationPromise
    }

    this.initializationPromise = this.performInitialization()
    return this.initializationPromise
  }

  private async performInitialization(): Promise<void> {
    try {
      // Wait for both clients to be ready
      const readyPromises: Promise<void>[] = []

      if (this.userClient) {
        readyPromises.push(
          this.userClient.ready().catch((e) => console.error('User client ready error:', e))
        )
      }

      if (this.orgClient) {
        readyPromises.push(
          this.orgClient.ready().catch((e) => console.error('Org client ready error:', e))
        )
      }

      await Promise.all(readyPromises)
      this.isInitialized = true
      console.log('FeatureFlagManager initialized successfully')
    } catch (error) {
      console.error('Failed to initialize FeatureFlagManager:', error)
      throw error
    }
  }

  public async checkToggleFlag(flagName: FeatureFlag): Promise<boolean> {
    // Ensure initialization is complete before checking flags
    await this.initialize()

    const featureFlagConfig = FEATURE_FLAG_CONFIG[flagName]
    if (!featureFlagConfig) {
      throw new Error(
        `Feature configuration not found for ${flagName}. Results could be incorrect...`
      )
    }

    let treatment: SplitIO.Treatment = 'control'

    if (featureFlagConfig.level === TrafficType.USER) {
      treatment = this.userClient?.getTreatment(flagName) ?? 'control'
    } else if (featureFlagConfig.level === TrafficType.ORG) {
      treatment = this.orgClient?.getTreatment(flagName) ?? 'control'
    } else {
      throw new Error('Feature configuration incorrect.')
    }

    // A defaultOn flag is on unless Split answers 'off' (missing flag, or no answer yet).
    return featureFlagConfig.defaultOn ? treatment !== 'off' : treatment === 'on'
  }

  // Helper method to manually refresh the cache (optional)
  public async refresh(): Promise<void> {
    this.isInitialized = false
    this.initializationPromise = undefined
    await this.initialize()
  }

  // Clean up method for proper resource disposal
  public destroy(): void {
    try {
      this.userClient?.destroy()
      this.orgClient?.destroy()
    } catch (error) {
      console.error('Error destroying FeatureFlagManager:', error)
    }
  }

  // public async checkFlag(flagName: FeatureFlag): Promise<string> {
  //   await this.userClient.ready().catch((e) => console.error(e))

  //   const treatment: SplitIO.Treatment = this.userClient.getTreatment(flagName)
  //   return treatment
  // }
}

// Singleton pattern to manage FeatureFlagManager instances
class FeatureFlagManagerSingleton {
  private instances: Map<string, FeatureFlagManager> = new Map()

  private getInstanceKey(userId?: string, tenantId?: string): string {
    return `${userId || 'default'}-${tenantId || 'default'}`
  }

  public getInstance(userId?: string, tenantId?: string): FeatureFlagManager {
    const key = this.getInstanceKey(userId, tenantId)

    if (!this.instances.has(key)) {
      const instance = new FeatureFlagManager(userId, tenantId)
      this.instances.set(key, instance)
      console.log(`Created new FeatureFlagManager instance for key: ${key}`)
    }

    return this.instances.get(key)!
  }

  public async checkToggleFlag(
    flagName: FeatureFlag,
    userId?: string,
    tenantId?: string
  ): Promise<boolean> {
    const instance = this.getInstance(userId, tenantId)
    return instance.checkToggleFlag(flagName)
  }

  // Clean up specific instance
  public destroyInstance(userId?: string, tenantId?: string): void {
    const key = this.getInstanceKey(userId, tenantId)
    const instance = this.instances.get(key)

    if (instance) {
      instance.destroy()
      this.instances.delete(key)
      console.log(`Destroyed FeatureFlagManager instance for key: ${key}`)
    }
  }

  // Clean up all instances
  public destroyAll(): void {
    this.instances.forEach((instance, key) => {
      instance.destroy()
      console.log(`Destroyed FeatureFlagManager instance for key: ${key}`)
    })
    this.instances.clear()
  }

  // Get current instance count (for debugging)
  public getInstanceCount(): number {
    return this.instances.size
  }
}

// Export singleton instance
export const featureFlagManager = new FeatureFlagManagerSingleton()

// Export convenience function for direct usage
export const checkFeatureFlag = (
  flagName: FeatureFlag,
  userId?: string,
  tenantId?: string
): Promise<boolean> => {
  return featureFlagManager.checkToggleFlag(flagName, userId, tenantId)
}
